import React, { useContext, useEffect, useMemo, useState } from "react";
import "./SalaryApproval.css";
import SalaryApprovalDetail from "./SalaryApprovalDetail";
import { UserContext } from "../../../contexts/CurrentUserContext";
import { getSalaryApprovalsByApproverV2 } from "../../../api/timesheetApi";

function SalaryApproval() {
  const { userData } = useContext(UserContext);

  const formatMonthYear = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "На рассмотрении":
        return "⏳";
      case "Согласован":
        return "✓";
      case "Отклонено":
        return "✗";
      default:
        return "";
    }
  };

  const getStatusClassName = (status) => {
    switch (status) {
      case "На рассмотрении":
        return "status-pending";
      case "Согласован":
        return "status-approved";
      case "Отклонено":
        return "status-rejected";
      default:
        return "";
    }
  };

  const getRowClassName = (department) => {
    if (!department.approvers || department.approvers.length === 0) {
      return "";
    }

    const statuses = department.approvers.map((a) => a.status);
    const hasRejected = statuses.some((s) => s === "Отклонено");
    const hasPending = statuses.some((s) => s === "На рассмотрении");
    const allApproved = statuses.every((s) => s === "Согласован");

    if (hasRejected) {
      return "salary-table__row--rejected";
    }
    if (allApproved) {
      return "salary-table__row--approved";
    }
    if (hasPending) {
      return "salary-table__row--pending";
    }
    return "";
  };

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeStatus = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
      case "согласован":
        return "Согласован";
      case "rejected":
      case "declined":
      case "отклонено":
      case "не согласован":
        return "Отклонено";
      case "pending":
      case "на рассмотрении":
      default:
        return "На рассмотрении";
    }
  };

  const formatPersonShortName = (person) => {
    if (!person) return "-";
    const surname = person.surname || "";
    const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
    return `${surname} ${firstInitial}`.trim() || "-";
  };

  useEffect(() => {
    const loadApprovals = async () => {
      if (!userData?.id) {
        setDepartments([]);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const approvals = await getSalaryApprovalsByApproverV2(userData.id);
        const mappedDepartments = approvals.map((approval) => ({
          id: approval.department?.id ?? approval.id,
          approvalId: approval.id,
          name: approval.department?.name || "Неизвестный отдел",
          initiator: formatPersonShortName(approval.initiator),
          approvers: (approval.steps || []).map((step, index) => ({
            role: `Согласующий ${index + 1}`,
            name: formatPersonShortName(step.approver),
            status: normalizeStatus(step.status),
            comment: step.comment || "",
            decidedAt: step.decided_at ? new Date(step.decided_at) : null,
          })),
          submissionDate: approval.created_at ? new Date(approval.created_at) : null,
          approvedAt: approval.approved_at ? new Date(approval.approved_at) : null,
          status: normalizeStatus(approval.status),
          year: approval.year,
          month: approval.month,
          date: new Date(approval.year, Math.max(0, Number(approval.month) - 1), 1),
        }));
        setDepartments(mappedDepartments);
      } catch (err) {
        console.error("Ошибка при загрузке согласований зарплаты:", err);
        setError(err.message || "Ошибка при загрузке данных");
        setDepartments([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadApprovals();
  }, [userData?.id]);

  // Фильтрация по дате
  const filteredDepartments = useMemo(() => {
    if (!selectedDate) {
      return departments;
    }

    return departments.filter((dept) => {
      const deptMonth = dept.date.getMonth();
      const deptYear = dept.date.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedYear = selectedDate.getFullYear();

      return deptMonth === selectedMonth && deptYear === selectedYear;
    });
  }, [selectedDate, departments]);

  // Получаем уникальные даты для фильтра
  const uniqueDates = useMemo(() => {
    const dates = new Set();
    departments.forEach((dept) => {
      const key = `${dept.date.getFullYear()}-${dept.date.getMonth()}`;
      dates.add(key);
    });
    return Array.from(dates)
      .map((key) => {
        const [year, month] = key.split("-").map(Number);
        return new Date(year, month, 1);
      })
      .sort((a, b) => b - a); // Сортировка от новых к старым
  }, [departments]);

  // Если выбран отдел, показываем детальный вид
  if (selectedDepartment) {
    return (
      <SalaryApprovalDetail
        department={selectedDepartment}
        onBack={() => setSelectedDepartment(null)}
      />
    );
  }

  return (
    <div className="salary-approval">
      <h2 className="salary-approval__title">Согласование зарплаты</h2>

      {/* Фильтр по дате */}
      <div className="salary-approval__filter">
        <label htmlFor="date-filter" className="salary-approval__filter-label">
          Фильтр по дате:
        </label>
        <select
          id="date-filter"
          className="salary-approval__filter-select"
          value={
            selectedDate
              ? `${selectedDate.getFullYear()}-${selectedDate.getMonth()}`
              : ""
          }
          onChange={(e) => {
            if (e.target.value) {
              const [year, month] = e.target.value.split("-").map(Number);
              setSelectedDate(new Date(year, month, 1));
            } else {
              setSelectedDate(null);
            }
          }}
        >
          <option value="">Все периоды</option>
          {uniqueDates.map((date, index) => (
            <option
              key={index}
              value={`${date.getFullYear()}-${date.getMonth()}`}
            >
              {formatMonthYear(date)}
            </option>
          ))}
        </select>
      </div>

      {/* Таблица */}
      {isLoading ? (
        <div className="salary-table__empty">
          <p>Загрузка данных...</p>
        </div>
      ) : error ? (
        <div className="salary-table__empty">
          <p>Ошибка: {error}</p>
        </div>
      ) : filteredDepartments.length === 0 ? (
        <div className="salary-table__empty">
          <p>Нет данных для отображения</p>
        </div>
      ) : (
        <div className="salary-table__container">
          <table className="salary-table">
            <thead>
              <tr>
                <th>Отдел</th>
                <th>Инициатор</th>
                <th>Дата</th>
                <th>Статус</th>
                <th>Дата отправки на согласование</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepartments.map((department) => (
                <tr
                  key={department.approvalId ?? department.id}
                  className={`salary-table__row ${getRowClassName(department)}`}
                  onClick={() => setSelectedDepartment(department)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="salary-table__department">
                    {department.name}
                  </td>
                  <td className="salary-table__initiator">
                    {department.initiator || "-"}
                  </td>
                  <td className="salary-table__date">
                    <span className="salary-table__date-icon">📅</span>
                    {formatMonthYear(department.date)}
                  </td>
                  <td className="salary-table__status">
                    {department.approvers && department.approvers.length > 0 ? (
                      <div className="salary-table__status-list">
                        {department.approvers.map((approver, index) => (
                          <div
                            key={index}
                            className={`salary-table__approver ${getStatusClassName(
                              approver.status,
                            )}`}
                          >
                            <span className="salary-table__approver-role">
                              {approver.role} ({approver.name}):
                            </span>
                            <span
                              className={`salary-table__approver-status ${getStatusClassName(
                                approver.status,
                              )}`}
                            >
                              <span className="salary-table__status-icon">
                                {getStatusIcon(approver.status)}
                              </span>
                              {approver.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="salary-table__no-status">-</span>
                    )}
                  </td>
                  <td className="salary-table__submission-date">
                    {department.submissionDate ? (
                      <>
                        <span className="salary-table__date-icon">📅</span>
                        {formatDate(department.submissionDate)}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SalaryApproval;
